import React, { useContext, useState } from 'react';
import {
  Query,
  ReferenceArrayInput,
  ReferenceInput,
  required,
  AutocompleteInput,
  TextInput,
  RadioButtonGroupInput,
  FormDataConsumer,
  useGetList,
  Loading,
  Error
} from 'react-admin';
import { Grid, Typography, makeStyles } from '@material-ui/core';
import { FormSpy } from 'react-final-form';

import DoubleListArrayInput from '../../../core/components/inputs/DoubleListArrayInput';
import { DistributionListContext } from './DistributionListContext';
import VirtualizedGrid from '../../../core/components/VirtualizedGrid';
import { excelColumns } from './RecipientsLookups';
import ImportExcelGroup from '../../../core/components/ImportExcelGroup';
import { fieldLengthValidator } from '../../../core/utils/index';
import CustomSimpleForm from '../../../core/components/form/CustomSimpleForm';

const useStyles = makeStyles({
  header: {
    paddingTop: '0px',
    fontSize: 20,
    fontWeight: 600
  },
  description: {
    display: 'flex',
    marginTop: '7px',
    fontSize: 14
  },
  radioContainer: {
    '& span': {
      marginTop: '-17px',
      fontSize: '14px'
    }
  },
  gridItem: {
    height: 'calc(100vh - 400px)'
  },
  gridLarge: {
    maxWidth: '47.83333%',
    flexBasis: '47.83333%',
    display: 'flex',
    flexDirection: 'row'
  },
  loading: {
    alignSelf: 'center'
  }
});

const radioContainer = {
  marginTop: '-25px'
};

const validateFunds = value => {
  if (!value || (value && value.length < 1)) {
    return 'At least one Fund must be selected';
  }
  return undefined;
};

const emptySubject = value => {
  const subjectValid = !!value.trim();

  if (!subjectValid) {
    return `Subject can't be empty space(s)`;
  }
};

/**
 * Wrap DoubleListArrayInput with list of fund families ==> groups property
 */
const FundDoubleList = ({
  classes,
  fundTagsLoading,
  fundTagsError,
  ...props
}) => (
  <Query type="GET_LIST" resource="fundfamilies" payload={{}}>
    {({ data, _total, loading, error }) => {
      if (loading || fundTagsLoading) {
        return <Loading className={classes.loading} />;
      } else if (error || fundTagsError) {
        return <Error />;
      } else {
        return (
          <DoubleListArrayInput
            {...props}
            groups={data}
            groupId="id"
            groupLabel="name"
            groupBy="fundFamilyId"
            filterByGroup={true}
            filterByGroupLabel="All Fund Families"
            tagsList={Object.values(props.tagsList)}
            allowTagFilter={true}
            source={'fundtags'}
          />
        );
      }
    }}
  </Query>
);

const RecipientsStep = props => {
  const { dlContext, setDlContext } = useContext(DistributionListContext);
  const classes = useStyles();
  const {
    setIsNextAvailable,
    variant,
    onSubmitEnd,
    setPreselectedUsersEmails,
    setUserIdList,
    permissions
  } = props;

  const { distributionList = { funds: [] } } = dlContext;

  const hasImportPermission =
    permissions && permissions.includes('AUTH_IMPORT_DISTRIBUTION_LIST_EXCEL');

  const data = [
    {
      rowIndex: 'Row Number',
      email: 'Email',
      validationError: 'Validation Error'
    }
  ];

  const [showGrid, setShowGrid] = useState(false);

  const setRecipients = async values => {
    const newDistributionList = {
      name: values.name,
      templateId: values.templateId,
      funds: values.funds
    };

    setDlContext({
      ...dlContext,
      currentForm: undefined,
      distributionList: newDistributionList
    });
    onSubmitEnd();
  };

  const createCellStyle = (rowData, color) => {
    return {
      ...rowData.style,
      color: color,
      fontSize: '14px',
      fontWeight: '400',
      textAlign: 'left',
      background: 'white',
      display: 'flex',
      alignItems: 'center',
      marginLeft: '3px',
      borderTop: '1px solid #E0E0E0',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis'
    };
  };

  const columns = excelColumns.map(col => ({
    id: col.id,
    source: col.id,
    type: 'text',
    renderer: params => {
      const { column, row_data, rowVal } = params;
      const cellStyle = createCellStyle(row_data, col.color);
      return (
        <span style={cellStyle} key={col.id}>
          {rowVal[column.source]}
        </span>
      );
    }
  }));

  const {
    data: fundTags,
    loading: fundTagsLoading,
    error: fundTagsError
  } = useGetList('fundtags', null, undefined, {
    orderBy: 'name',
    orderDirection: 'ASC'
  });

  return (
    <CustomSimpleForm
      save={setRecipients}
      toolbar={null}
      resource={props.resource}
      variant={variant}
      margin="normal"
      initialValues={{ ...distributionList, recipientsChoice: 'FUND' }}
    >
      <FormSpy subscription={{ pristine: true }}>
        {formprops => {
          // Store current form API object
          if (dlContext.currentForm === undefined) {
            setDlContext({ ...dlContext, currentForm: formprops.form });
          }

          return (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="h5" className={classes.header}>
                  Create List of Recipients
                </Typography>
                <span className={classes.description}>
                  Set the Details of the Distribution list such as name, email
                  template. Create List of Recipients by selecting Funds.
                </span>
              </Grid>
              <Grid item xs={12}>
                <Grid container justifyContent="space-between">
                  <Grid item xs={6} className={classes.gridLarge}>
                    <TextInput
                      label="Distribution List Name"
                      source="name"
                      resource={props.resource}
                      validate={[
                        required(),
                        value => emptySubject(value),
                        fieldLengthValidator(75)
                      ]}
                      options={{ autoComplete: 'off' }}
                      variant={variant}
                      helperText={false}
                    />
                  </Grid>
                  <Grid item xs={6} className={classes.gridLarge}>
                    <ReferenceInput
                      label="Email Template"
                      source="templateId"
                      reference="mailtemplates"
                      filter={{ onlyInfo: true }}
                      variant={variant}
                      helperText={false}
                      perPage={-1}
                    >
                      <AutocompleteInput 
                        optionText="description"
                        validate={[required()]} 
                        helperText={false}
                        options={{
                          suggestionsContainerProps: {
                            style: {
                              backgroundColor: 'white',
                              border: '1px solid #E0E0E0',
                              boxShadow:' 0px 5px 5px -3px rgba(0,0,0,0.2), 0px 8px 10px 1px rgba(0,0,0,0.14), 0px 3px 14px 2px rgba(0,0,0,0.12)',
                            }
                          }
                        }}
                      />
                    </ReferenceInput>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12} sm={12} md={6}>
                <div style={radioContainer}>
                  {hasImportPermission && (
                    <RadioButtonGroupInput
                      source="recipientsChoice"
                      label=""
                      options={{ row: true }}
                      margin="none"
                      choices={[
                        { id: 'FUND', name: 'Select from Funds' },
                        { id: 'FILE', name: 'Import from Excel' }
                      ]}
                      variant={variant}
                      helperText={false}
                      onChange={value => {
                        setIsNextAvailable(value !== 'FILE');
                        setShowGrid(false);
                        setPreselectedUsersEmails([]);
                        setUserIdList([]);
                      }}
                    />
                  )}
                </div>
              </Grid>
              <Grid item xs={12} className={classes.gridItem}>
                <FormDataConsumer>
                  {({ formData, ..._rest }) =>
                    formData &&
                    formData.recipientsChoice === 'FUND' && (
                      <ReferenceArrayInput
                        label="Funds"
                        resource={props.resource}
                        reference="fundsDistributionList"
                        source="funds"
                        perPage={-1}
                        sort={{ field: 'name', order: 'ASC' }}
                        filter={{
                          onlyFundsWithFundInvestorRelation: 'true',
                          returnFundTags: 'true'
                        }}
                        validate={[validateFunds, required()]}
                      >
                        <FundDoubleList
                          classes={classes}
                          tagsList={fundTags}
                          fundTagsLoading={fundTagsLoading}
                          fundTagsError={fundTagsError}
                        />
                      </ReferenceArrayInput>
                    )
                  }
                </FormDataConsumer>
                <FormDataConsumer>
                  {({ formData, ..._rest }) =>
                    formData &&
                    formData.recipientsChoice === 'FILE' && (
                      <ImportExcelGroup
                        setIsNextAvailable={setIsNextAvailable}
                        {...props}
                      />
                    )
                  }
                </FormDataConsumer>
                {showGrid && (
                  <VirtualizedGrid
                    columns={columns}
                    data={data}
                    fixedColumns={0}
                    height={'calc(100vh - 350px)'}
                    minHeight="400px"
                    rowHeight={35}
                  />
                )}
              </Grid>
            </Grid>
          );
        }}
      </FormSpy>
    </CustomSimpleForm>
  );
};

export default RecipientsStep;
